import { useForm, useStore } from '@tanstack/react-form'
import clsx from 'clsx'
import map from 'lodash/map'
import uniq from 'lodash/uniq'
import React, { useEffect, useMemo, useRef } from 'react'

import { ampli } from '@Analytics/ampli'
import SvgCheck from '@Assets/SVG/Check'
import { ImageV4 } from '@Components/Image'
import { InputErrors, InputGroup, InputGroupAddon, InputGroupBase, InputGroupIcon, InputGroupInput, InputGroupLabel, metaToFieldState } from '@Components/Input'
import { NativeModalBody, NativeModalFooter, NativeModalHeader } from '@Components/Modal/NativeModal/NativeModal'
import { stripEmoji } from '@Components/Personalization/Util/helpers'
import { Button } from '@CoreComponents/Buttons/Button'
import { CheckboxIndicator } from '@CoreComponents/Checkboxes/checkboxIndicator'
import { Disclaimer } from '@Domains/Cart/Components/Prompt/TextPersonalization/Disclaimer'
import { TextPersonalizationTypes } from '@Domains/Cart/Components/Prompt/TextPersonalization/types'
import { ProductCrossSellAndPersonalizationContentFragment } from '@Generated/graphql'
import { getPersonalization } from '@Globals/CMS/ProductHelpers'
import { useLocale } from '@Globals/Locale'
import { useTranslation } from '@Globals/Translations'

import styles from './styles.module.scss'

export const TextPersonalizationPrompt = ({
  availablePersonalizations,
  initialValue,
  onSubmit,
  selectedPersonalization,
  title,
  type,
  useUpdateFlow,
}: {
  availablePersonalizations: ProductCrossSellAndPersonalizationContentFragment[]
  initialValue?: string
  onSubmit: (productID: string | null, personalization?: { text: string }) => Promise<void>
  selectedPersonalization: ProductCrossSellAndPersonalizationContentFragment | null
  /** Overrides the default "Engraving/Debossing options" title (e.g. with a progress stepper). */
  title?: React.ReactNode
  type: TextPersonalizationTypes
  useUpdateFlow?: boolean
}) => {
  const { t } = useTranslation()

  const [personalizations, defaultValues] = useMemo(() => {
    const defaultPersonalization = useUpdateFlow && selectedPersonalization && initialValue ? getPersonalization(selectedPersonalization) : undefined

    const list: ReturnType<typeof getPersonalization>[] = []
    const defaults = {
      selected: defaultPersonalization?.id || undefined,
    }

    availablePersonalizations?.forEach((i) => {
      const item = getPersonalization(i)
      if (item) {
        list.push(item)
        defaults[item.id] = defaultPersonalization?.id === item.id ? initialValue : ''
      }
    })

    return [list, defaults]
  }, [availablePersonalizations, initialValue, useUpdateFlow, selectedPersonalization])

  const formValidator = ({ value }) => {
    if (typeof value.selected === 'undefined') {
      return t('cms:INPUT_FIELD_IS_REQUIRED')
    }

    return undefined
  }

  const form = useForm({
    defaultValues,
    onSubmit: async ({ value }) => {
      if (value.selected === null) {
        return onSubmit(null)
      }

      const selected = personalizations.find(p => p.id === value.selected)
      return onSubmit(selected?.id, { text: value?.[value.selected] || '' })
    },
    validators: {
      onChange: formValidator,
      onMount: formValidator,
    },
  })
  const selectedPrice = useStore(form.store, (state) => {
    const selected = personalizations.find(p => p.id === state.values.selected)
    if (!selected) {
      return ''
    }

    if (selected.discountedPrice) {
      return `${selected?.discountedPrice} - `
    }

    if (selected.price) {
      return `${selected?.price} - `
    }

    return ''
  })

  const buttonRef = useRef<HTMLSpanElement | null>(null)

  const locale = useLocale()

  useEffect(() => { ampli.promptOpen({ source_id: `${type}_prompt` }) }, [type])

  return (
    <form
      className={styles.textPersonalizationForm}
      onSubmit={(e) => {
        e.preventDefault()
        form.handleSubmit()
      }}
    >
      <NativeModalHeader className={title ? styles.stepperHeader : undefined}>
        {title || (
          <div className={styles.title}>
            {type === 'deboss' && t('products:DEBOSSING_OPTIONS')}
            {type === 'engrave' && t('products:ENGRAVING_OPTIONS')}
          </div>
        )}
      </NativeModalHeader>
      <NativeModalBody>
        <div className={styles.textPersonalizationPrompt}>
          <div className={styles.content}>
            {map(personalizations, personalization => (
              <div key={`${type}-${personalization.id}`} className={styles.accordionItem}>
                <form.Field name="selected">
                  {field => (
                    <input
                      checked={field.state.value === personalization.id}
                      className={styles.accordionItemCheckbox}
                      id={`${type}-${personalization.id}`}
                      name={field.name}
                      onClick={(e) => {
                        field.handleChange(personalization.id)
                        e.currentTarget?.parentElement?.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'start' })
                        buttonRef.current?.scrollIntoView({ behavior: 'smooth', inline: 'nearest', block: 'nearest' })
                      }}
                      type="radio"
                    />
                  )}
                </form.Field>

                <div className={styles.accordionHeaderAndContent}>
                  <label htmlFor={`${type}-${personalization.id}`}>
                    {personalization.image && <ImageV4 draggable={false} src={personalization.image} width={250} />}
                    <div className={styles.label}>
                      <form.Subscribe selector={state => state.values.selected}>
                        {selected => <CheckboxIndicator checked={selected === personalization.id} round style={{ marginRight: '12px' }} />}
                      </form.Subscribe>
                      {personalization.placement[locale]}
                    </div>
                  </label>
                  <div className={styles.accordionContent}>
                    <div className={styles.inputWrapper}>
                      <p className={styles.inputLabel}>{t('products:ADD_YOUR_TEXT_HERE')}</p>
                      <form.Field
                        name={personalization.id}
                        validators={{
                          onChange: ({ fieldApi, value }) => {
                            if (fieldApi.form.getFieldValue('selected') !== personalization.id) {
                              return undefined
                            }

                            const text = (value || '').toString()
                            if (text.length === 0 /* && fieldApi.form.getFieldMeta(personalization.id)?.isTouched */) {
                              return t('cms:TEXT_PERSONALIZATION_INPUT_ERROR', { context: 'REQUIRED' })
                            }

                            const textWithoutEmojis = stripEmoji(text)
                            if (textWithoutEmojis !== text) {
                              return t('cms:TEXT_PERSONALIZATION_INPUT_ERROR', { context: 'EMOJIS' })
                            }

                            const maxCharacters = personalization.maxCharacters || 0
                            if (Array.from(text).length > maxCharacters) {
                              return t('cms:TEXT_PERSONALIZATION_INPUT_ERROR', { context: 'MAX_LENGTH', maxCharacters })
                            }

                            if (type === 'deboss') {
                              const invalidCharacters = textWithoutEmojis.match(/([^a-zA-Z0-9-,;:!?.'"()[\]{}@*/&#%+<>~$áÁàÀåÅäÄæÆéÉóÓöÖøØúÚüÜ\s])/gu)
                              if (invalidCharacters && invalidCharacters.length > 0) {
                                return t('cms:TEXT_PERSONALIZATION_INPUT_ERROR', {
                                  context: 'CHARACTERS',
                                  invalidCharacters: uniq(invalidCharacters).sort().join(' '),
                                })
                              }
                            }

                            return undefined
                          },
                          onChangeListenTo: ['selected'],
                        }}
                      >
                        {field => (
                          <>
                            <InputGroup state={metaToFieldState(field.state.meta, field.state.value)}>
                              <InputGroupBase>
                                <InputGroupLabel
                                  htmlFor={`${type}-${personalization.id}-input`}
                                  id={`${type}-${personalization.id}-label`}
                                >
                                  {t('TEXT_MAX_X_CHARACTERS').replace('<span>X</span>', `${personalization.maxCharacters || 'X'}`)}
                                </InputGroupLabel>
                                <InputGroupInput
                                  aria-invalid={field.state.meta.isValid === false && (field.state.meta.isTouched || field.state.meta.isDefaultValue)}
                                  aria-labelledby={`${type}-${personalization.id}-label`}
                                  id={`${type}-${personalization.id}-input`}
                                  name={field.name}
                                  onBlur={field.handleBlur}
                                  onChange={e => field.handleChange(e.target.value)}
                                  value={field.state.value as string}
                                />
                              </InputGroupBase>
                              {field.state.meta.isValid && (field.state.meta.isTouched || field.state.meta.isDefaultValue) && (
                                <InputGroupAddon align="inline-end">
                                  <InputGroupIcon>
                                    <SvgCheck fill="var(--green-200)" />
                                  </InputGroupIcon>
                                </InputGroupAddon>
                              )}
                            </InputGroup>
                            <InputErrors
                              errors={field.state.meta.errors}
                              isTouched={field.state.meta.isTouched}
                              isValid={field.state.meta.isValid}
                            />
                          </>
                        )}
                      </form.Field>
                    </div>
                    <Disclaimer type={type} />
                  </div>
                </div>
              </div>
            ))}
            <div className={styles.accordionItem}>
              <form.Field name="selected">
                {field => (
                  <input
                    checked={field.state.value === null}
                    className={styles.accordionItemCheckbox}
                    id={`${type}-none`}
                    name={field.name}
                    onClick={() => {
                      field.handleChange(null)
                      buttonRef.current?.scrollIntoView({ behavior: 'smooth', inline: 'nearest', block: 'nearest' })
                    }}
                    type="radio"
                  />
                )}
              </form.Field>

              <div className={styles.accordionHeaderAndContent}>
                <label htmlFor={`${type}-none`}>
                  <div className={clsx(styles.label, styles.deSelect)}>
                    <form.Subscribe selector={state => state.values.selected}>
                      {selected => <CheckboxIndicator checked={selected === null} round style={{ marginRight: '12px' }} />}
                    </form.Subscribe>
                    {type === 'deboss' && t('cart:NO_DEBOSSING')}
                    {type === 'engrave' && t('cart:NO_ENGRAVING')}
                    {type !== 'deboss' && type !== 'engrave' && 'TODO: No selection'}
                  </div>
                </label>
              </div>
            </div>
          </div>

          <span ref={buttonRef} />
        </div>
      </NativeModalBody>
      <NativeModalFooter className={styles.footer}>
        <form.Subscribe selector={state => [state.canSubmit, state.isSubmitting]}>
          {([canSubmit, isSubmitting]) => (
            <Button
              disabled={!canSubmit}
              fillWidth
              innerSpacing="medium"
              loading={isSubmitting}
              theme="black"
              type="submit"
            >
              {selectedPrice}
              {t(useUpdateFlow ? 'cms:UPDATE_LABEL' : 'cms:ADD_TO_CART')}
            </Button>
          )}
        </form.Subscribe>
      </NativeModalFooter>
    </form>
  )
}
